import { Component, HostListener, OnInit } from '@angular/core';
import { Invoice } from '../invoice';
import { InvoiceService } from '../invoice.service';
import { faAngleLeft, faSortUp, faSortDown, faFilter, faTriangleExclamation, faRotateLeft, faFileDownload, faSortAmountDownAlt } from '@fortawesome/free-solid-svg-icons';
import { InvoiceStatusPipe } from '../invoice-status.pipe';
import { Location, NgIf, NgTemplateOutlet, NgFor, CurrencyPipe } from '@angular/common';
import { TableControlService } from '../table-control.service';
import { environment } from 'src/environments/environment';
import { Subject, debounceTime } from 'rxjs';
import { NgbModal, NgbTooltip, NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, NgbDropdownButtonItem, NgbDropdownItem, NgbPopover, NgbPagination, NgbCollapse } from '@ng-bootstrap/ng-bootstrap';
import { FormatDateTimePipe } from '../format-date-time.pipe';
import { RouterLink } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';

@Component({
    selector: 'app-invoices',
    templateUrl: './invoices.component.html',
    styleUrls: ['./invoices.component.css'],
    providers: [InvoiceStatusPipe],
    imports: [NgbTooltip, FaIconComponent, NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, NgbDropdownButtonItem, NgbDropdownItem, NgIf, NgTemplateOutlet, NgbPopover, NgFor, RouterLink, NgbPagination, NgbCollapse, CurrencyPipe, FormatDateTimePipe, InvoiceStatusPipe]
})
export class InvoicesComponent implements OnInit {
  //Screensize event Listener
  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.resizeSubject.next((event.target as Window).innerWidth);
  }

  // Variable Subject
  private resizeSubject: Subject<number> = new Subject<number>();

  // Properties
  invoices: Invoice[] = [];
  invoiceCount: number = 0;
  isMobileView: boolean = false;

  sortOptions = [
    { label: 'Invoice #', value: 'i.invoice_number' },
    { label: 'Client', value: 'c.client_name' },
    { label: 'Project', value: 'proj.project_name' },
    { label: 'Amount', value: 'i.invoice_amount' },
    { label: 'Status', value: 'i.invoice_status' },
    { label: 'Issue Date', value: 'i.invoice_issue_date' },
    { label: 'Due Date', value: 'i.invoice_due_date' },
  ];
  filterColumns = [
    { label: 'Client', value: 'c.client_name' },
    { label: 'Project', value: 'proj.project_name' },
    { label: 'Status', value: 'i.invoice_status' },
  ];
  sortBy: string = 'i.invoice_number';
  sortDirection: string = 'ASC';

  // Table Controls
  tableControls: TableControlService;
  storedSearchValue: string;

  // Filters
  columnFilterValues = new Map();
  activeFilters = new Map();
  activeFilterCount: number = 0;
  selectedFilterColumn: string ='';
  hasActiveFiltersValue: boolean = false;

  // Font Awesome Properties
  faAngleLeft = faAngleLeft;
  faSortUp = faSortUp;
  faSortDown = faSortDown;
  faFilter = faFilter;
  faTriangleExclamation = faTriangleExclamation;
  faRotateLeft = faRotateLeft;
  faFileDownload = faFileDownload;
  faSortAmountDownAlt = faSortAmountDownAlt;

  // Get isMobile value in reactive way
  public get isMobile(): boolean {
    return this.isMobileView;
  }

  constructor(private invoiceService: InvoiceService,
    private invoiceStatusPipe: InvoiceStatusPipe,
    private modalService: NgbModal,
    public location: Location,
  ) {
      this.resizeSubject.pipe(
        debounceTime(100)
      ).subscribe(width => {
        if (width <= 768) {
          this.checkScreenSize(width);
        } else {
          this.isMobileView = false;
          this.tableControls.setLimit(20);
        }
      });
    }

  ngOnInit(): void {
    this.tableControls = new TableControlService('Invoices', true, 'i.invoice_number', 'DESC');
    this.checkScreenSize();
    if(this.isMobile) {
      let activeFilters = this.tableControls.getActiveFilters()
      let columns = [];
      for (const [key, value] of activeFilters) {
        columns.push(key)
      }
      this.tableControls.resetFilters(columns)
    }
    this.tableControls.refresh.subscribe(() => {
      this.getInvoices();
      this.updateHasActiveFilters();
    });
    if (this.tableControls.hasTableConfiguration()) this.storedSearchValue = this.tableControls.getSearchTerm();
    this.getInvoices();
    // Initialize hasActiveFilters after first load
    setTimeout(() => this.updateHasActiveFilters(), 0);
  }

  ngOnDestroy(): void {
    this.resizeSubject.unsubscribe();
  }

  // Get Invoices
  private getInvoices(): void {
    let params = this.tableControls.getParams();
    if(this.isMobile) {
      params.sortBy = this.sortBy;
      params.sortDirection = this.sortDirection;
      for (const [key, value] of this.activeFilters) {
        if (key.includes('range')) params[`filter:${key}`] = value;
        else params[`filter:${key}`] = JSON.stringify(value);
      }
    };
    this.invoiceService.getInvoices(params).subscribe((res) => {
      this.invoices = res.invoices;
      this.invoiceCount = res.count;
      this.updateHasActiveFilters();
    });
  }

  updateHasActiveFilters(): void {
    // Only show if there are results
    if (this.invoiceCount === 0) {
      this.hasActiveFiltersValue = false;
      return;
    }
    if (this.isMobile) {
      this.hasActiveFiltersValue = this.activeFilters.size > 0 || (this.tableControls.getSearchTerm() && this.tableControls.getSearchTerm().length > 0);
    } else {
      const tableFilters = this.tableControls.getActiveFilters();
      this.hasActiveFiltersValue = tableFilters.size > 0 || (this.tableControls.getSearchTerm() && this.tableControls.getSearchTerm().length > 0);
    }
  }

  get hasActiveFilters(): boolean {
    return this.hasActiveFiltersValue;
  }

  // Download Invoices (All)
  downloadInvoices(): void {
    window.open(`${environment.apiUrl}/invoices/export`);
  }

  // Download Invoices (Filtered)
  downloadInvoicesFiltered(): void {
    let params = this.tableControls.getParams();
    if (this.isMobile) {
      params.sortBy = this.sortBy;
      params.sortDirection = this.sortDirection;
      for (const [key, value] of this.activeFilters) {
        if (key.includes('range')) params[`filter:${key}`] = value;
        else params[`filter:${key}`] = JSON.stringify(value);
      }
    }
    delete params.limit;
    delete params.offset;
    // Build query string
    const queryParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== null && value !== undefined) {
        queryParams.append(key, value.toString());
      }
    }
    const queryString = queryParams.toString();
    window.open(`${environment.apiUrl}/invoices/export${queryString ? '?' + queryString : ''}`);
  }

  /* ----- Filters and Sorting ----- */

  // Get Distinct Column Values
  getDistinctColumnValues(column: string): void {
    this.selectedFilterColumn = column;
    if (!this.columnFilterValues.has(column)) {
      const params = {
        searchTerm: null,
        sortBy: column,
        sortDirection: 'ASC',
        limit: null,
        offset: null,
        column: column
      };
      if(this.isMobile) {
        for (const [key, value] of this.activeFilters) {
          if (key.includes('range')) params[`filter:${key}`] = value;
          else params[`filter:${key}`] = JSON.stringify(value);
        }
      }
      this.invoiceService.getDistinctColumnValues(params).subscribe((values) => {
        const tempArray = [];
        for (const value of values) {
          let name = null;
          switch (column) {
            case 'i.invoice_status':
              name = this.invoiceStatusPipe.transform(value);
              break;
            default:
              name = (value === null || value.length == 0) ? 'BLANK' : value;
              break;
          }
          tempArray.push({ name: name, value: value, randomId: Math.random().toString(36) });
        }
        this.columnFilterValues.set(column, tempArray);
      });
    }
  }

   // Sorting
   sorting(column: string): void {
    if (column != this.sortBy) this.sortDirection = 'ASC';
    if (column == this.sortBy) this.sortDirection = (this.sortDirection == 'ASC') ? 'DESC' : 'ASC';
    this.sortBy = column;
    this.getInvoices();
  }

  // Toggle Filter
  toggleFilter(column: string, value: string): void {
    if (this.activeFilters.has(column)) {
      if (this.activeFilters.get(column).includes(value)) {
        let tempArray = this.activeFilters.get(column);
        tempArray = tempArray.filter((filterValue) => { return filterValue != value; });
        if (tempArray.length == 0) {
          this.activeFilters.delete(column);
        } else {
          this.activeFilters.set(column, tempArray);
        }
      } else {
        const tempArray = this.activeFilters.get(column);
        tempArray.push(value);
        this.activeFilters.set(column, tempArray);
      }
    } else {
      this.activeFilters.set(column, [value]);
    }
    this.getInvoices();
    this.getActiveFilterCount();
    this.updateHasActiveFilters();
  }

   // Get Active Filter Count
   getActiveFilterCount(): void {
    this.activeFilterCount = 0;
    for (const value of this.activeFilters.values()) {
      this.activeFilterCount += value.length;
    }
  }

  // Filter Exists
  filterExists(column: string, value: string): boolean {
    return this.activeFilters.has(column) ? this.activeFilters.get(column).includes(value) : false;
  }

  // Reset Active Filters
  resetActiveFilters(): void {
    this.activeFilters.clear();
    this.getInvoices();
    this.getActiveFilterCount();
    this.updateHasActiveFilters();
  }

  // Show Column Filter Popover
  showColumnFilterPopover(popover, column: string): void {
    this.getDistinctColumnValues(column);
    if (popover.isOpen()) {
      popover.close();
    } else {
      setTimeout(() => {
        popover.open({ column });
      }, 250);
    }
  }

  //Set isMobile value and set table limit base on screen size
  checkScreenSize(width?: number) {
    let screenWidth = width || window.innerWidth;
    this.isMobileView =  screenWidth <= 768;
    this.isMobileView ? this.tableControls.setLimit(5): this.tableControls.setLimit(20);
  }
}
